import { useMutation } from "react-query";
import { axiosInstance } from "./axios";
import { INewTag } from "./casesService";
import { ISearch } from "modules/actionsModule";
import { IError } from "dto/IError";
import { ITask } from "dto/ITask";
import { IFeed } from "dto/IFeed";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { IBusyDate, ICalendar } from "dto/IBusyDate";

const queryKeys = {
  getFeed: (
    start: string,
    end: string,
    caseId?: string,
    search?: ISearch,
    calendarIds?: string[],
    onlyFromCourtPortal?: boolean
  ) => [
    "taskService.getFeed",
    start,
    end,
    caseId,
    search,
    calendarIds,
    onlyFromCourtPortal,
  ],
  getBusyForTasks: (start: string, end: string, calendarIds: string[]) => [
    "taskService.getBusyForTasks",
    start,
    end,
    calendarIds,
  ],
  getBusyForEvents: (
    start: string,
    end: string,
    calendarIds: string[],
    onlyFromCourtPortal?: boolean
  ) => [
    "taskService.getBusyForEvents",
    start,
    end,
    calendarIds,
    onlyFromCourtPortal,
  ],
  getTasks: (page: number) => ["taskService.getTasks", page],
  getSingleTask: (taskId: string) => ["taskService.getSingleTask", taskId],
  getCalendars: () => ["taskService.getCalendars"],
};

const getFeed = (
  start: string,
  end: string,
  caseId?: string,
  search?: ISearch,
  calendarIds?: string[],
  onlyFromCourtPortal?: boolean
): Promise<{ data: IFeed[] }> => {
  const query = new URLSearchParams(window.location.search);
  query.append("start", start);
  query.append("end", end);

  if (caseId) {
    query.append("caseId", caseId);
  }

  if (search) {
    if (search.text.length) {
      query.append("Text", search.text);
    }

    if (search.statusId) {
      query.append("StatusId", search.statusId);
    }
    if (search.tagId) {
      query.append("TagId ", search.tagId);
    }
    if (search.entityId) {
      query.append("AssignedUserId ", search.entityId);
    }
  }

  if (calendarIds) {
    calendarIds.forEach((c) => {
      query.append("calendarId", c);
    });
  }

  if (onlyFromCourtPortal) {
    query.append("fromCourtPortal", "true");
  }

  return axiosInstance.get(`/event/Feed?${query}`);
};

const useGetFeed = (
  start: string,
  end: string,
  caseId?: string,
  search?: ISearch,
  calendarIds?: string[],
  onlyFromCourtPortal?: boolean
) => {
  return useCustomQuery(
    queryKeys.getFeed(
      start,
      end,
      caseId,
      search,
      calendarIds,
      onlyFromCourtPortal
    ),
    () => getFeed(start, end, caseId, search, calendarIds, onlyFromCourtPortal)
  );
};

export interface IUpdateTaskPayload {
  Allday: boolean;
  FromDate: string;
  Id: null | string;
  IsFavourite: boolean;
  Note: string;
  TagIds: string[];
  Title?: string;
  NewTags?: INewTag[];
  ToDate: null | string;
  OwnerId: string;
  OwnerKind: number;
  WorkspaceId?: string;
  ForUserId?: string;
  CalendarId?: string;
  isTask?: boolean;
}

const updateTask = (payload: IUpdateTaskPayload): Promise<{ data: ITask }> => {
  if (payload.isTask) {
    payload.isTask = undefined;
    return axiosInstance.post("/task", payload);
  }
  payload.isTask = undefined;
  return axiosInstance.post("/event", payload);
};

const useUpdateTask = () => {
  return useCustomMutation(updateTask);
};

const newUpdateTask = (
  payload: IUpdateTaskPayload
): Promise<{ data: ITask }> => {
  return axiosInstance.put("/event", payload);
};

const useNewUpdateTask = () => {
  return useCustomMutation(newUpdateTask);
};

const getTasks = (page: number): Promise<{ data: ITask[] }> => {
  return axiosInstance.get(
    `Task/List?Page=${page}&SortProp=Deadline&SortOrder=Ascending`
  );
};

const useGetTasks = (page: number) => {
  return useCustomQuery(queryKeys.getTasks(page), () => getTasks(page));
};

const getCalendars = (): Promise<{ data: ICalendar[] }> => {
  return axiosInstance.get(`/task/calendars`);
};

const useGetCalendars = () => {
  return useCustomQuery(queryKeys.getCalendars(), () => getCalendars());
};

const getBusyForEvents = (
  start: string,
  end: string,
  calendarIds: string[],
  onlyFromCourtPortal?: boolean
): Promise<{ data: IBusyDate[] }> => {
  const query = new URLSearchParams(window.location.search);
  query.append("start", start);
  query.append("end", end);

  if (calendarIds) {
    calendarIds.forEach((c) => {
      query.append("calendarId", c);
    });
  }

  if (onlyFromCourtPortal) {
    query.append("fromCourtPortal", "true");
  }

  return axiosInstance.get(`/event/Busy?${query}`);
};

const useGetBusyForEvents = (
  start: string,
  end: string,
  calendarIds?: string[],
  onlyFromCourtPortal?: boolean
) => {
  return useCustomQuery(
    queryKeys.getBusyForEvents(start, end, calendarIds, onlyFromCourtPortal),
    () => getBusyForEvents(start, end, calendarIds, onlyFromCourtPortal)
  );
};

const getBusyForTasks = (
  start: string,
  end: string,
  calendarIds: string[]
): Promise<{ data: IBusyDate[] }> => {
  const query = new URLSearchParams(window.location.search);
  query.append("start", start);
  query.append("end", end);

  if (calendarIds) {
    calendarIds.forEach((c) => {
      query.append("calendarId", c);
    });
  }

  return axiosInstance.get(`/tasks/Busy?${query}`);
};

const useGetBusyForTasks = (
  start: string,
  end: string,
  calendarIds?: string[]
) => {
  return useCustomQuery(
    queryKeys.getBusyForTasks(start, end, calendarIds),
    () => getBusyForTasks(start, end, calendarIds)
  );
};

const complete = (payload: {
  Id?: string;
  ExternalEmailId?: string;
}): Promise<{ data: IFeed }> => {
  return axiosInstance.post("/event/Completed", payload);
};

const useComplete = () => {
  return useCustomMutation(complete);
};

const unComplete = (payload: {
  Id?: string;
  ExternalEmailId?: string;
}): Promise<{ data: IFeed }> => {
  return axiosInstance.post("/event/Uncompleted", payload);
};

const useUncomplete = () => {
  return useCustomMutation(unComplete);
};

export interface IGetTask {
  OwnerId?: string;
  OwnerKind?: number;
}

const getTask = (payload: IGetTask): Promise<{ data: IFeed }> => {
  const query = new URLSearchParams(window.location.search);

  query.append("OwnerId", payload.OwnerId!);
  query.append("OwnerKind", payload.OwnerKind!.toString());

  return axiosInstance.get(`/event/list?${query}`);
};

const useGetTask = (payload: IGetTask) => {
  return useCustomQuery(["tasks", payload], () => getTask(payload));
};

const deleteTask = (payload: {
  Id: string;
  MoveToTrash: boolean;
}): Promise<{ data: IFeed }> => {
  return axiosInstance.delete(`task`, { data: payload });
};

const useDeleteTask = () => {
  return useCustomMutation(deleteTask);
};

export interface IMoveTask {
  AllDay: boolean;
  End: string;
  Start: string;
  Id: string;
}

const moveTask = (payload: IMoveTask): Promise<{ data: IFeed }> => {
  return axiosInstance.post("/event/Move", payload);
};

const useMoveTask = () => {
  return useCustomMutation(moveTask);
};

const getSingleTask = (taskId: string): Promise<{ data: ITask }> => {
  return axiosInstance.get(`/task/get?id=${taskId}`);
};

const useGetSingleTask = (taskId: string) => {
  return useCustomQuery(queryKeys.getSingleTask(taskId), () =>
    getSingleTask(taskId)
  );
};

const getHistory = (taskId: string): Promise<{ data: { Items: ITask[] } }> => {
  return axiosInstance.get(`/task/history?id=${taskId}`);
};

const useGetHistory = (taskId: string) => {
  return useCustomQuery(["historytask", taskId], () => getHistory(taskId));
};

const removeTask = (payload: {
  Id: string;
  MoveToTrash: boolean;
}): Promise<{ data: IError }> => {
  return axiosInstance.delete("/task", {
    data: payload,
  });
};

const useRemoveTask = () => {
  return useCustomMutation(removeTask);
};

export interface ICreateComment {
  TaskId: string;
  Note: string;
}

const createComment = (payload: ICreateComment): Promise<{ data: ITask }> => {
  return axiosInstance.post("/task/comment", payload);
};

const useCreateComment = () => {
  return useCustomMutation(createComment);
};

export interface IRemoveComment {
  Id: string;
}

const removeComment = (payload: IRemoveComment) => {
  return axiosInstance.delete("/task/comment", {
    data: payload,
  });
};

const useRemoveComment = () => {
  return useMutation(removeComment);
};

export interface IUpdateComment {
  Id: string;
  Note: string;
}

const updateComment = (payload: IUpdateComment) => {
  return axiosInstance.put("/task/comment", {
    data: payload,
  });
};

const useUpdateComment = () => {
  return useMutation(updateComment);
};

const updateEvent = (payload: IUpdateTaskPayload): Promise<{ data: ITask }> => {
  return axiosInstance.put("/task", payload);
};

const useUpdateEvent = () => {
  return useCustomMutation(updateEvent);
};

export default {
  useGetFeed,
  useUpdateTask,
  useGetTasks,
  useGetBusyForEvents,
  useGetBusyForTasks,
  useComplete,
  useUncomplete,
  useGetTask,
  useDeleteTask,
  useMoveTask,
  useNewUpdateTask,
  useGetSingleTask,
  useGetHistory,
  useRemoveTask,
  useCreateComment,
  useUpdateEvent,
  useRemoveComment,
  useUpdateComment,
  useGetCalendars,
};
